import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Box, Paper, Typography, Divider, Stack } from '@mui/material';
import { People, DirectionsCar, Groups } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { ShellOutletContext } from '../../utils/presence';

export default function DispatchHomePage() {
  const { user } = useAuth();
  const { online } = useOutletContext<ShellOutletContext>();
  const [agencyName, setAgencyName] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [positionName, setPositionName] = useState('');

  useEffect(() => {
    fetch('/api/system-config/logo', { method: 'HEAD' })
      .then((r) => { if (r.ok) setLogoUrl('/api/system-config/logo'); })
      .catch(() => {});
    fetch('/api/system-config/public')
      .then((r) => r.json())
      .then((d) => setAgencyName(d.agency_name || d.jurisdiction_name || ''))
      .catch(() => {});

    const posId = localStorage.getItem('operator_position_id');
    const storedName = localStorage.getItem('operator_position_name');
    if (storedName) {
      setPositionName(storedName);
    } else if (posId) {
      fetch('/api/operator-positions/active')
        .then((r) => r.json())
        .then((positions: { id: number; name: string }[]) => {
          const match = (positions || []).find((p) => String(p.id) === posId);
          if (match) setPositionName(match.name);
        })
        .catch(() => {});
    }
  }, []);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 140px)' }}>
      <Paper sx={{ p: 6, textAlign: 'center', maxWidth: 560, width: '100%' }}>
        {logoUrl && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Box component="img" src={logoUrl} alt="Agency Logo" sx={{ maxHeight: 160, maxWidth: 320, objectFit: 'contain' }} />
          </Box>
        )}
        {agencyName && (
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>{agencyName}</Typography>
        )}
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
          WatchCenter CAD — Dispatch
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'center' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Logged in as</Typography>
            <Typography variant="h6">{user?.full_name || user?.username}</Typography>
            {user?.full_name && (
              <Typography variant="body2" color="text.secondary">{user?.username}</Typography>
            )}
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Operator Position</Typography>
            <Typography variant="h6">{positionName || 'None'}</Typography>
          </Box>
        </Box>
        <Divider sx={{ mt: 3, mb: 2 }} />
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Currently Online
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 2, justifyContent: 'center' }}>
          <OnlineStat icon={<People />} value={online.dispatch_users} label="Dispatch" />
          <OnlineStat icon={<DirectionsCar />} value={online.mdt_users} label="MDT" />
          <OnlineStat icon={<Groups />} value={online.total_users} label="Total" />
        </Stack>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Use the menu to open the Map / Dispatch and Units &amp; Calls windows.
        </Typography>
      </Paper>
    </Box>
  );
}

function OnlineStat({ icon, value, label }: { icon: React.ReactNode; value: number; label: string }) {
  return (
    <Box sx={{ minWidth: 90 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', color: '#64b5f6', mb: 0.5 }}>{icon}</Box>
      <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  );
}
